import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Schema } from "@bw/shared/types/generated";
import { formatDate } from "@bw/shared/utils";
import { useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import { getAppeal, resolveAppeal } from "../api";

type Decision = Schema["AppealResolutionRequest"]["decision"];

export function ModerationAppealDetailPage() {
  const { appealId = "" } = useParams<{ appealId: string }>();
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<Decision | null>(null);
  const inFlight = useRef(false);
  const appealQuery = useQuery({
    queryKey: ["platform", "appeals", "detail", appealId],
    queryFn: () => getAppeal(appealId),
    enabled: Boolean(appealId),
  });
  const resolution = useMutation({
    mutationFn: (next: Decision) => resolveAppeal(appealId, next),
    onSuccess: (data) => {
      queryClient.setQueryData<Schema["PlatformAppealDetailResponse"]>(
        ["platform", "appeals", "detail", appealId],
        (current) =>
          current
            ? {
                ...current,
                status: data.appeal.status,
                resolved_at: data.appeal.resolved_at,
                ban_is_active:
                  data.effect === "lifted" || data.effect === "already_inactive"
                    ? false
                    : current.ban_is_active,
              }
            : current,
      );
      void queryClient.invalidateQueries({
        queryKey: ["platform", "appeals", "detail", appealId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["platform", "appeals"],
      });
      void queryClient.invalidateQueries({ queryKey: ["platform", "bans"] });
      setDecision(null);
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });
  const appeal = appealQuery.data
    ? resolution.data
      ? {
          ...appealQuery.data,
          status: resolution.data.appeal.status,
          resolved_at: resolution.data.appeal.resolved_at,
          ban_is_active:
            resolution.data.effect === "lifted" ||
            resolution.data.effect === "already_inactive"
              ? false
              : appealQuery.data.ban_is_active,
        }
      : appealQuery.data
    : undefined;

  if (appealQuery.isLoading) {
    return (
      <PlatformPage title="Appeal detail">
        <Stack
          role="status"
          aria-label="Loading appeal"
          sx={{ alignItems: "center", py: 8 }}
        >
          <CircularProgress size={32} />
        </Stack>
      </PlatformPage>
    );
  }
  if (!appeal) {
    return (
      <PlatformPage title="Appeal detail">
        <Alert
          severity="error"
          role="alert"
          action={
            <Button color="inherit" onClick={() => void appealQuery.refetch()}>
              Try again
            </Button>
          }
        >
          We couldn&apos;t load this appeal.
        </Alert>
      </PlatformPage>
    );
  }

  const label = decision === "lift_ban" ? "Lift ban" : "Uphold ban";
  const hasDecisionContext = Boolean(
    appeal.ban_reason?.trim() &&
      appeal.ban_source?.trim() &&
      appeal.ban_issued_at,
  );
  return (
    <>
      <PlatformPage title="Appeal detail">
        <Stack spacing={3}>
          <Button
            component={RouterLink}
            to="/moderation/appeals"
            variant="text"
            sx={{ alignSelf: "flex-start" }}
          >
            Back to appeals
          </Button>
          {appealQuery.isRefetchError ? (
            <Alert
              severity="warning"
              role="alert"
              action={
                <Button
                  color="inherit"
                  onClick={() => void appealQuery.refetch()}
                >
                  Try again
                </Button>
              }
            >
              {resolution.data
                ? "The decision is saved, but refresh failed. The confirmed result remains shown."
                : "Refresh failed. The last loaded appeal remains shown."}
            </Alert>
          ) : null}
          {resolution.isSuccess ? (
            <Alert severity="success" role="status">
              {resolution.data.effect === "lifted"
                ? "Ban lifted."
                : resolution.data.effect === "upheld"
                  ? "Ban upheld."
                  : "The restriction was already inactive; no ban decision was applied."}
            </Alert>
          ) : null}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ justifyContent: "space-between" }}
              >
                <Typography component="h2" variant="h6">
                  {appeal.reason}
                </Typography>
                <StatusBadge status={appeal.status} />
              </Stack>
              <Typography color="text.secondary" variant="body2">
                Submitted {formatDate(appeal.created_at)}
              </Typography>
              <Typography>{appeal.appellant_display_name}</Typography>
              <Stack spacing={0.25}>
                <Typography color="text.secondary" variant="overline">
                  Ban reason
                </Typography>
                <Typography>{appeal.ban_reason}</Typography>
              </Stack>
              <Stack spacing={0.25}>
                <Typography color="text.secondary" variant="overline">
                  Issued
                </Typography>
                <Typography>{formatDate(appeal.ban_issued_at)}</Typography>
              </Stack>
              <Stack spacing={0.25}>
                <Typography color="text.secondary" variant="overline">
                  Source
                </Typography>
                <Typography>{appeal.ban_source}</Typography>
              </Stack>
              <Typography color="text.secondary">
                {appeal.ban_is_active
                  ? appeal.ban_expires_at
                    ? `Active restriction until ${formatDate(appeal.ban_expires_at)}`
                    : "Active restriction · Permanent"
                  : "Restriction is no longer active"}
              </Typography>
              {!hasDecisionContext ? (
                <Alert severity="warning" role="alert">
                  Ban decision context is unavailable. Refresh before resolving
                  this appeal.
                </Alert>
              ) : null}
              {appeal.status === "pending" ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    disabled={!hasDecisionContext}
                    onClick={() => {
                      resolution.reset();
                      setDecision("uphold_ban");
                    }}
                  >
                    Uphold ban
                  </Button>
                  <Button
                    variant="outlined"
                    color="success"
                    disabled={!hasDecisionContext}
                    onClick={() => {
                      resolution.reset();
                      setDecision("lift_ban");
                    }}
                  >
                    Lift ban
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      </PlatformPage>

      <Dialog
        open={decision !== null}
        onClose={() => !resolution.isPending && setDecision(null)}
        aria-labelledby="appeal-resolution-title"
      >
        <DialogTitle id="appeal-resolution-title">{label}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {decision === "lift_ban"
              ? "The active ban will be lifted and this appeal will be closed."
              : "The ban will remain active and this appeal will be closed."}
          </DialogContentText>
          {resolution.isError ? (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              We couldn&apos;t save this decision. Please try again.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={resolution.isPending}
            onClick={() => setDecision(null)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={decision === "lift_ban" ? "success" : "primary"}
            disabled={resolution.isPending || !hasDecisionContext}
            onClick={() => {
              if (!decision || inFlight.current) return;
              inFlight.current = true;
              resolution.mutate(decision);
            }}
          >
            {resolution.isPending
              ? decision === "lift_ban"
                ? "Lifting…"
                : "Saving…"
              : label}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
